import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Save, FileText, Layout, Music, Video, 
  GraduationCap, Users, Calendar, Loader2, Check
} from 'lucide-react';
import { motion } from 'framer-motion';
import { adminFetch, isAdminAuthenticated } from '@/utils/adminAuth';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Define all editable content sections
const CONTENT_SECTIONS = [
  {
    id: 'homepage',
    name: 'Homepage',
    icon: Layout,
    fields: [
      { key: 'hero_title', label: 'Hero Title', type: 'text', default: 'Escola de Samba Casa Samba' },
      { key: 'hero_subtitle', label: 'Hero Subtitle', type: 'textarea', default: 'Experience the rhythm of Brasil' },
      { key: 'hero_button_text', label: 'Hero Button Text', type: 'text', default: 'Explore Music' },
      { key: 'featured_section_title', label: 'Featured Section Title', type: 'text', default: 'Featured Music' },
      { key: 'about_section_title', label: 'About Section Title', type: 'text', default: 'About Escola de Samba Casa Samba' },
      { key: 'about_section_text', label: 'About Section Text', type: 'textarea', default: 'Escola de Samba Casa Samba brings authentic Afro-Brasilian drumming, dance, and culture...' },
    ]
  },
  {
    id: 'music_store',
    name: 'Music Library',
    icon: Music,
    fields: [
      { key: 'music_page_title', label: 'Page Title', type: 'text', default: 'Music Library' },
      { key: 'music_page_subtitle', label: 'Page Subtitle', type: 'textarea', default: 'Discover authentic Brasilian rhythms' },
      { key: 'tracks_section_title', label: 'Tracks Section', type: 'text', default: 'Individual Tracks' },
      { key: 'albums_section_title', label: 'Albums Section', type: 'text', default: 'Full Albums' },
    ]
  },
  {
    id: 'video_store',
    name: 'Video Store',
    icon: Video,
    fields: [
      { key: 'video_page_title', label: 'Page Title', type: 'text', default: 'Video Store' },
      { key: 'video_page_subtitle', label: 'Page Subtitle', type: 'textarea', default: 'Watch and learn from the masters' },
      { key: 'featured_videos_title', label: 'Featured Videos Title', type: 'text', default: 'Featured Performances' },
    ]
  },
  {
    id: 'courses',
    name: 'Courses',
    icon: GraduationCap,
    fields: [
      { key: 'courses_page_title', label: 'Page Title', type: 'text', default: 'Escola de Samba Casa Samba Academy' },
      { key: 'courses_page_subtitle', label: 'Page Subtitle', type: 'textarea', default: 'Master the art of Afro-Brasilian drumming, dance, and culture' },
      { key: 'courses_section_title', label: 'Courses Section', type: 'text', default: 'Our Courses' },
      { key: 'certification_section_title', label: 'Certification Section', type: 'text', default: 'Certification Pathway' },
    ]
  },
  {
    id: 'membership',
    name: 'Membership',
    icon: Users,
    fields: [
      { key: 'membership_page_title', label: 'Page Title', type: 'text', default: 'Membership' },
      { key: 'membership_page_subtitle', label: 'Page Subtitle', type: 'textarea', default: 'Join our community and unlock exclusive content' },
      { key: 'membership_cta_text', label: 'CTA Text', type: 'text', default: 'Choose Your Plan' },
    ]
  },
  {
    id: 'classes',
    name: 'Classes',
    icon: Calendar,
    fields: [
      { key: 'classes_page_title', label: 'Page Title', type: 'text', default: 'Live Classes' },
      { key: 'classes_page_subtitle', label: 'Page Subtitle', type: 'textarea', default: 'Join our in-person and virtual classes' },
      { key: 'schedule_section_title', label: 'Schedule Section', type: 'text', default: 'Class Schedule' },
    ]
  },
];

export const AdminSiteContent = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('homepage');
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (!isAdminAuthenticated()) {
      navigate('/admin');
      return;
    }
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const response = await adminFetch('/admin/site-content');
      if (response.ok) {
        const data = await response.json();
        // Convert array to object keyed by content key
        const contentMap = {};
        data.forEach(item => {
          contentMap[item.key] = item.value;
        });
        setContent(contentMap);
      }
    } catch (error) {
      console.error('Failed to load content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setContent(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save all content for current section
      const section = CONTENT_SECTIONS.find(s => s.id === activeSection);
      const updates = section.fields.map(field => ({
        key: field.key,
        value: content[field.key] || field.default,
        section: activeSection
      }));

      const response = await adminFetch('/admin/site-content', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });

      if (response.ok) {
        toast.success('Content saved successfully!');
        setHasChanges(false);
      } else {
        toast.error('Failed to save content');
      }
    } catch (error) {
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const currentSection = CONTENT_SECTIONS.find(s => s.id === activeSection);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="p-2 hover:bg-card rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold">Site Content Editor</h1>
                <p className="text-sm text-muted-foreground">Edit the text on your website pages</p>
              </div>
            </div>
            
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-full font-medium transition-all ${
                hasChanges 
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90' 
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : hasChanges ? (
                <Save className="w-4 h-4" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              {saving ? 'Saving...' : hasChanges ? 'Save Changes' : 'Saved'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-8">
          {/* Sidebar - Section List */}
          <div className="w-64 shrink-0">
            <div className="sticky top-24 space-y-2">
              <h3 className="text-sm font-semibold text-muted-foreground mb-4 px-3">PAGES</h3>
              {CONTENT_SECTIONS.map((section) => {
                const Icon = section.icon;
                const isActive = activeSection === section.id;
                
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      isActive 
                        ? 'bg-primary text-primary-foreground' 
                        : 'hover:bg-card text-foreground'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{section.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Main Content - Edit Fields */}
          <div className="flex-1">
            <motion.div
              key={activeSection}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-card border border-border rounded-2xl p-8"
            >
              <div className="flex items-center gap-3 mb-8">
                {currentSection && <currentSection.icon className="w-6 h-6 text-primary" />}
                <h2 className="text-xl font-bold">{currentSection?.name} Content</h2>
              </div>

              <div className="space-y-6">
                {currentSection?.fields.map((field) => (
                  <div key={field.key} className="space-y-2">
                    <label className="block text-sm font-semibold text-foreground">
                      {field.label}
                    </label>
                    
                    {field.type === 'textarea' ? (
                      <textarea
                        value={content[field.key] || field.default}
                        onChange={(e) => handleChange(field.key, e.target.value)}
                        rows={4}
                        className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                        placeholder={field.default}
                      />
                    ) : (
                      <input
                        type="text"
                        value={content[field.key] || field.default}
                        onChange={(e) => handleChange(field.key, e.target.value)}
                        className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder={field.default}
                      />
                    )}
                    
                    <p className="text-xs text-muted-foreground">
                      This text appears on the {currentSection?.name.toLowerCase()} page
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Preview hint */}
            <div className="mt-6 p-4 bg-primary/10 border border-primary/20 rounded-xl">
              <p className="text-sm text-primary">
                <strong>Tip:</strong> After saving, refresh the page you edited to see your changes live on the site.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSiteContent;
